import api from '../api/client';
import React, { useState, useEffect } from 'react';
import {
    Form,
    InputNumber,
    Input,
    Select,
    Button,
    Upload,
    DatePicker,
    Divider,
    Space,
    Card,
    Table,
    Typography,
    message,
    Row,
    Col,
    Spin
} from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import config from '../config';

const { Title, Text } = Typography;

export default function AnimalSaleForm({ stakeholders, onChange, employees }) {
    const [form] = Form.useForm();
    const [deliveredMethod, setDeliveredMethod] = useState('picked');
    const [items, setItems] = useState([{ species: '', category: '', identifier: '', quantity: 1, weight: 1, pricePerAnimal: 0 }]);
    const [availableAnimals, setAvailableAnimals] = useState({});
    const [loadingAnimals, setLoadingAnimals] = useState(false);
    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
    const apiUrl = config[environment].apiUrl;

    const speciesOptions = [
        { value: 'goats', label: 'Goats' },
        { value: 'pigs', label: 'Pigs' },
        { value: 'poultry', label: 'Poultry' },
        { value: 'sheep', label: 'Sheep' },
        { value: 'weaners', label: 'Weaners' }
    ];

    const categoryOptions = {
        goats: [
            { value: 'adult', label: 'Adult Goat' },
            { value: 'kid', label: 'Kid' },
            { value: 'buck', label: 'Buck' },
            { value: 'doe', label: 'Doe' }
        ],
        pigs: [
            { value: 'sow', label: 'Sow' },
            { value: 'boar', label: 'Boar' },
            { value: 'piglet', label: 'Piglet' },
            { value: 'grower', label: 'Grower' }
        ],
        poultry: [
            { value: 'layers', label: 'Layers' },
            { value: 'broilers', label: 'Broilers' },
            { value: 'chicks', label: 'Chicks' },
            { value: 'roosters', label: 'Roosters' }
        ],
        sheep: [
            { value: 'ram', label: 'Ram' },
            { value: 'ewe', label: 'Ewe' },
            { value: 'lamb', label: 'Lamb' }
        ],
        weaners: [
            { value: 'goats_weaner', label: 'Goat Weaner' },
            { value: 'pigs_weaner', label: 'Pig Weaner' },
            { value: 'sheep_weaner', label: 'Sheep Weaner' }
        ]
    };

    const paymentOptions = [
        { value: 'unpaid', label: 'On Credit' },
        { value: 'cash', label: 'On Cash' }
    ];

    const fetchAvailableAnimals = async (species) => {
        if (availableAnimals[species]) return; // Already fetched

        setLoadingAnimals(true);
        try {
            let endpoint = '';

            switch (species) {
                case 'goats':
                    endpoint = '/goats';
                    break;
                case 'pigs':
                    endpoint = '/pigs';
                    break;
                case 'poultry':
                    endpoint = '/poultry';
                    break;
                case 'sheep':
                    endpoint = '/sheep';
                    break;
                case 'weaners':
                    // Weaners might need a different approach - fetch all types
                    await fetchWeaners();
                    setLoadingAnimals(false);
                    return;
                default:
                    return;
            }

            const response = await api.get(`${endpoint}`);
setAvailableAnimals((prev) => ({
                ...prev,
                [species]: response.data
            }));
        } catch (error) {
            console.error(`Error fetching ${species}:`, error);
            message.error(`Failed to load available ${species}`);
        } finally {
            setLoadingAnimals(false);
        }
    };
// Fetch weaners by species type
    const fetchWeaners = async () => {
        try {
            const weanerTypes = ['goats', 'pigs', 'sheep'];
            const weanersData = {};

            for (const type of weanerTypes) {
                try {
                    const response = await api.get(`/api/weaners/${type}`);
                    // const response = await api.get(`/api/weaners/available?type=${type}`);
                    // const response = await api.get(`/api/${type}/available`);
weanersData[type] = response.data;
                } catch (error) {
                    console.error(`Error fetching ${type} weaners:`, error);
                    weanersData[type] = [];
                }
            }

            setAvailableAnimals((prev) => ({
                ...prev,
                weaners: weanersData
            }));
        } catch (error) {
            console.error('Error fetching weaners:', error);
            message.error('Failed to load available weaners');
        }
    };

    // Get available identifiers based on species and category
    const getAvailableIdentifiers = (species, category, currentIndex) => {
        const usedIdentifiers = items
            .filter((_, index) => index !== currentIndex)
            .map((item) => item.identifier)
            .filter(Boolean);

        if (!availableAnimals[species]) return [];

        let available = [];

        if (species === 'poultry') {
            // For poultry, show batch numbers
            const poultryData = availableAnimals[species] || [];
            available = poultryData
                .filter((animal) => !category || animal?.type?.toLowerCase() === category?.toLowerCase())
                .map((animal) => ({
                    value: animal.batch,
                    label: `Batch: ${animal.batch} (Available: ${animal.quantity || 1})`
                }));
        } else if (species === 'weaners') {
            // For weaners, show tag_numbers from the specific type
            const weanerType = category ? category.replace('_weaner', '') : null;
            const weanersData = availableAnimals?.weaners ? availableAnimals?.weaners[weanerType]?.data || [] : [];
available = weanersData
                .filter((animal) => animal.species?.toLowerCase() === weanerType?.toLowerCase())
                .map((animal) => ({
                    value: animal.tag_number,
                    label: `Tag: ${animal.tag_number} - ${animal.description || 'Weaner'} (Available: ${animal.quantity || 1})`
                }));
        } else {
            // For other species, show tagNumbers
            const speciesData = availableAnimals[species] || [];
            available = speciesData
                // .filter((animal) => !category || animal.category === category)
                .map((animal) => ({
                    value: animal.tagNumber,
                    label: `Tag: ${animal.tagNumber} - ${animal.breed || animal.description || 'Animal'} (Available: ${
                        animal.quantity || 1
                    })`
                }));
        }

        return available.filter((ident) => !usedIdentifiers.includes(ident.value));
    };

    // Get available species that haven't been fully used
    const getAvailableSpecies = (currentIndex) => {
        const selectedSpecies = items
            // .filter((_, index) => index !== currentIndex)
            .map((item) => item.species)
            .filter(Boolean);

        return speciesOptions.filter((species) => selectedSpecies.includes(species.value));
    };

    const addItem = () => {
        const lastItem = items[items.length - 1];

        // Validate last item before adding new one
        if (!lastItem.species) {
            message.error('Please select animal species');
            return;
        }

        if (!lastItem.category) {
            message.error('Please select animal category');
            return;
        }

        if (!lastItem.identifier) {
            message.error('Please select animal identifier');
            return;
        }

        if (!lastItem.quantity || lastItem.quantity <= 0) {
            message.error('Please enter quantity (must be greater than 0)');
            return;
        }

        if (!lastItem.weight || lastItem.weight <= 0) {
            message.error('Please enter weight (must be greater than 0)');
            return;
        }

        if (!lastItem.pricePerAnimal || lastItem.pricePerAnimal <= 0) {
            message.error('Please enter price per animal (must be greater than 0)');
            return;
        }

        const availableSpecies = getAvailableSpecies(-1);
        if (availableSpecies.length === 0) {
            message.warning('All animal species have been added');
            return;
        }

        setItems([
            ...items,
            {
                species: availableSpecies[0].value,
                category: '',
                identifier: '',
                quantity: 1,
                pricePerAnimal: 0,
                weight: 0
            }
        ]);
    };

    const removeItem = (index) => {
        if (items.length === 1) {
            message.warning('At least one animal item is required');
            return;
        }
        setItems(items.filter((_, i) => i !== index));
    };

    const updateItem = (index, key, value) => {
        const updated = [...items];

        // Reset dependent fields when species changes
        if (key === 'species') {
            updated[index].category = '';
            updated[index].identifier = '';
            // Fetch available animals for the selected species
            if (value) {
                fetchAvailableAnimals(value);
            }
        }

        // Reset identifier when category changes
        if (key === 'category') {
            updated[index].identifier = '';
        }

        updated[index][key] = value;
        setItems(updated);
    };

    // Compute total
    const totalAmount = items.reduce((sum, it) => sum + (it.weight || 0) * (it.pricePerAnimal || 0), 0);

    // Handle form values change
    const handleFormChange = () => {
        const formValues = form.getFieldsValue();
        onChange({
            saleType: 'animals',
            items,
            totalAmount,
            ...formValues
        });
    };

    useEffect(() => {
        handleFormChange();
    }, [items, deliveredMethod]);

    const computeSubtotal = (item) => {
        return Number(item.weight || 0) * Number(item.pricePerAnimal || 0);
    };

    const isAddButtonDisabled = () => {
        const lastItem = items[items.length - 1];
        const availableSpecies = getAvailableSpecies(-1);

        return (
            !lastItem.species ||
            !lastItem.category ||
            !lastItem.identifier ||
            !lastItem.quantity ||
            lastItem.quantity <= 0 ||
            !lastItem.weight ||
            lastItem.weight <= 0 ||
            !lastItem.pricePerAnimal ||
            lastItem.pricePerAnimal <= 0 ||
            availableSpecies.length === 0
        );
    };

    const getIdentifierLabel = (species) => {
        switch (species) {
            case 'poultry':
                return 'Batch Number';
            case 'weaners':
                return 'Tag Number';
            default:
                return 'Tag Number';
        }
    };

    const getAvailableQuantity = (species, category, identifier) => {
        if (!availableAnimals[species] || !identifier) return 0;
let animal;

        if (species === 'poultry') {
            const poultryData = availableAnimals[species] || [];
            // animal = poultryData.find((a) => a.batch === identifier);
            animal = poultryData.find((a) => a.batch === identifier && (!category || a.type?.toLowerCase() === category?.toLowerCase()));
        } else if (species === 'weaners') {
            const weanerType = category ? category.replace('_weaner', '') : null;
            const weanersData = availableAnimals.weaners ? availableAnimals.weaners[weanerType].data || [] : [];
            animal = weanersData?.find((a) => a.tag_number === identifier);
            // animal = weanersData.find((a) => a.tag_number === identifier && a.category === category);
        } else {
            const speciesData = availableAnimals[species] || [];
            animal = speciesData.find((a) => a.tagNumber === identifier);
            // animal = speciesData.find((a) => a.tagNumber === identifier && (!category || a.category === category));
        }

        return animal ? animal.quantity || 1 : 0;
    };

    const refreshAnimalData = async (species) => {
        if (species) {
            await fetchAvailableAnimals(species);
        } else {
            // Refresh all species
            const speciesToRefresh = items.map((item) => item.species).filter(Boolean);
            for (const spec of speciesToRefresh) {
                await fetchAvailableAnimals(spec);
            }
        }
    };

    return (
        <>
            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleFormChange}
                initialValues={{
                    saleDate: moment(),
                    deliveredMethod: 'picked'
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="stakeholderId" label="Customer" rules={[{ required: true }]}>
                            <Select placeholder="Select customer" showSearch optionFilterProp="children">
                                {stakeholders.map((s) => (
                                    <Select.Option key={s.id} value={s.id}>
                                        {s.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="saleDate" label="Sale Date" rules={[{ required: true }]}>
                            <DatePicker className="w-full" format="YYYY-MM-DD" onChange={handleFormChange} />
                        </Form.Item>
                    </Col>
                </Row>

                <Space direction="horizontal" style={{ width: '100%' }} align="start">
                    <Form.Item name="deliveredMethod" label="Delivery Method" rules={[{ required: true }]}>
                        <Select onChange={(value) => setDeliveredMethod(value)}>
                            <Select.Option value="delivered">Delivered</Select.Option>
                            <Select.Option value="picked">Picked at Farm</Select.Option>
                        </Select>
                    </Form.Item>

                    {deliveredMethod === 'delivered' && (
                        <Form.Item name="deliveryPersonId" label="Delivery Person" rules={[{ required: true }]}>
                            <Select placeholder="Select delivery person" showSearch optionFilterProp="children">
                                {employees.map((e) => (
                                    <Select.Option key={e.id} value={e.id}>
                                        {e.firstname} {e.lastname}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                </Space>

                <Divider />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        Animal Items
                    </Title>
                    <Button icon={<ReloadOutlined />} onClick={() => refreshAnimalData()} loading={loadingAnimals} size="small">
                        Refresh Animal Data
                    </Button>
                </div>

                {items.map((item, index) => {
                    const availableQuantity = getAvailableQuantity(item.species, item.category, item.identifier);
                    const maxQuantity = availableQuantity;

                    return (
                        // <Card
                        //     key={index}
                        //     style={{
                        //         border: '1px solid #d9d9d9',
                        //         marginBottom: 16,
                        //         borderRadius: 8
                        //     }}
                        //     size="small"
                        // >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Row gutter={16} align="middle">
                                <Col xs={24} sm={4}>
                                    <Form.Item
                                        label="Species"
                                        style={{ marginBottom: 0 }}
                                        validateStatus={!item.species ? 'error' : ''}
                                        help={!item.species ? 'Species is required' : ''}
                                    >
                                        <Select
                                            value={item.species}
                                            onChange={(v) => updateItem(index, 'species', v)}
                                            options={speciesOptions}
                                            // options={getAvailableSpecies(index)}
                                            placeholder="Select species"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={4}>
                                    <Form.Item
                                        label="Category"
                                        style={{ marginBottom: 0 }}
                                        validateStatus={!item.category ? 'error' : ''}
                                        help={!item.category ? 'Category is required' : ''}
                                    >
                                        <Select
                                            value={item.category}
                                            onChange={(v) => updateItem(index, 'category', v)}
                                            options={categoryOptions[item.species] || []}
                                            placeholder="Select category"
                                            disabled={!item.species}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={4}>
                                    <Form.Item
                                        label={getIdentifierLabel(item.species)}
                                        style={{ marginBottom: 0 }}
                                        validateStatus={!item.identifier ? 'error' : ''}
                                        help={!item.identifier ? `${getIdentifierLabel(item.species)} is required` : ''}
                                    >
                                        {loadingAnimals ? (
                                            <div style={{ padding: '8px 0' }}>
                                                <Spin size="small" /> Loading...
                                            </div>
                                        ) : (
                                            <Select
                                                value={item.identifier}
                                                onChange={(v) => updateItem(index, 'identifier', v)}
                                                options={getAvailableIdentifiers(item.species, item.category, index)}
                                                placeholder={`Select ${getIdentifierLabel(item.species).toLowerCase()}`}
                                                disabled={!item.species || !item.category}
                                                notFoundContent={
                                                    availableAnimals[item.species]
                                                        ? 'No available animals'
                                                        : 'Select species and category first'
                                                }
                                            />
                                        )}
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={4}>
                                    <Form.Item
                                        label="Quantity"
                                        style={{ marginBottom: 0 }}
                                        validateStatus={!item.quantity || item.quantity <= 0 || item.quantity > maxQuantity ? 'error' : ''}
                                        help={
                                            !item.quantity || item.quantity <= 0
                                                ? 'Must be greater than 0'
                                                : item.quantity > maxQuantity
                                                ? `Max: ${maxQuantity}`
                                                : ''
                                        }
                                    >
                                        <InputNumber
                                            placeholder="Qty"
                                            min={1}
                                            max={maxQuantity}
                                            value={item.quantity}
                                            onChange={(v) => updateItem(index, 'quantity', v)}
                                            style={{ width: '100%' }}
                                            disabled={!item.identifier}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={4}>
                                    <Form.Item
                                        label="Weight"
                                        style={{ marginBottom: 0 }}
                                        validateStatus={!item.weight || item.weight <= 0 ? 'error' : ''}
                                        help={!item.weight || item.weight <= 0 ? 'Must be greater than 0' : ''}
                                    >
                                        <InputNumber
                                            placeholder="weight"
                                            min={1}
                                            value={item.weight}
                                            onChange={(v) => updateItem(index, 'weight', v)}
                                            style={{ width: '100%' }}
                                            disabled={!item.identifier}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={4}>
                                    <Form.Item
                                        label="Price Each"
                                        style={{ marginBottom: 0 }}
                                        validateStatus={!item.pricePerAnimal || item.pricePerAnimal <= 0 ? 'error' : ''}
                                        help={!item.pricePerAnimal || item.pricePerAnimal <= 0 ? 'Must be greater than 0' : ''}
                                    >
                                        <InputNumber
                                            placeholder="Price"
                                            min={1}
                                            value={item.pricePerAnimal}
                                            onChange={(v) => updateItem(index, 'pricePerAnimal', v)}
                                            style={{ width: '100%' }}
                                            prefix="KES"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row justify="space-between" align="middle">
                                <Col>
                                    {item.identifier && availableQuantity > 0 && (
                                        <Text type="secondary">
                                            Available: {availableQuantity} {item.species}
                                        </Text>
                                    )}
                                    {item.species && !availableAnimals[item.species] && (
                                        <>
                                            <Text type="secondary" style={{ fontStyle: 'italic' }}>
                                                Click refresh to load available {item.species}
                                            </Text>
                                            <Button
                                                icon={<ReloadOutlined />}
                                                onClick={() => refreshAnimalData(item.species)}
                                                loading={loadingAnimals}
                                                size="small"
                                                type="text"
                                            />
                                        </>
                                    )}
                                </Col>
                                <Col>
                                    <Space>
                                        <Text strong>Subtotal: KES {computeSubtotal(item).toLocaleString()}</Text>
                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeItem(index)}
                                            size="small"
                                            disabled={items.length === 1}
                                        >
                                            Remove
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Space>
                        // </Card>
                    );
                })}
                <Divider />

                <Button type="dashed" icon={<PlusOutlined />} onClick={addItem} style={{ width: '100%' }} disabled={isAddButtonDisabled()}>
                    Add Animal Type
                </Button>
                <Divider />

                <Title level={4}>Total: KES {totalAmount.toLocaleString()}</Title>
                <Divider />

                <Form.Item name="images" label="Upload Images">
                    <Upload listType="picture" beforeUpload={() => false} multiple>
                        <Button icon={<UploadOutlined />}>Upload Images</Button>
                    </Upload>
                </Form.Item>

                <Form.Item name="paymentMethod" label="Payment Method" rules={[{ required: true }]}>
                    <Select placeholder="Select payment method" options={paymentOptions} />
                </Form.Item>
            </Form>
        </>
    );
}
